import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Segmented, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTmmdeImpactAssessments } from '@/hooks/useCalibration';
import type { TmmdeImpactAssessment } from '@/services/calibration.service';
import { formatDate, formatDateTime } from '@/utils/format/date';
import { IMPACT_TRIGGER_LABEL } from './tmmdeDisplay';

const { Title, Text } = Typography;

const TmmdeImpactListPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('OPEN');
  const { data = [], isLoading } = useTmmdeImpactAssessments({ status: status === 'ALL' ? undefined : status });

  const columns: ColumnsType<TmmdeImpactAssessment> = [
    {
      title: 'Assessment',
      dataIndex: 'assessment_number',
      width: 150,
      render: (v: string, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/tmmde/impact/${r.id}`)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Equipment',
      key: 'equipment',
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <span>{r.asset_code}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.asset_name}
            {r.serial_number ? ` · S/N ${r.serial_number}` : ''}
          </Text>
        </Space>
      ),
    },
    { title: 'Trigger', dataIndex: 'trigger_type', width: 190, render: (v: string) => IMPACT_TRIGGER_LABEL[v] || v },
    {
      title: 'Affected period',
      key: 'period',
      width: 200,
      render: (_: unknown, r) =>
        `${formatDate(r.period_from, { nullDisplay: 'First use' })} – ${formatDate(r.period_to)}`,
    },
    {
      title: 'Items',
      key: 'items',
      width: 150,
      render: (_: unknown, r) => {
        const pending = Number(r.pending_count || 0);
        return (
          <Space direction="vertical" size={0}>
            <span>{Number(r.item_count || 0)} affected</span>
            {pending > 0 && <Text type="warning">{pending} pending</Text>}
          </Space>
        );
      },
    },
    { title: 'Customers', dataIndex: 'customer_count', width: 110, render: (v) => Number(v || 0) },
    { title: 'NCR', dataIndex: 'ncr_number', width: 130, render: (v) => v || '—' },
    { title: 'Opened', dataIndex: 'created_at', width: 170, render: (v) => formatDateTime(v) },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => <Tag color={v === 'OPEN' ? 'orange' : 'green'}>{v === 'OPEN' ? 'Open' : 'Closed'}</Tag>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/tmmde')} style={{ marginBottom: 8 }}>
        Equipment List
      </Button>
      <Title level={2} style={{ marginTop: 0 }}>
        Equipment impact assessments
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Opened automatically when measuring equipment is found out of calibration, damaged or lost after it was used.
        Each affected inspection, work order, delivery and customer must be dispositioned before closure.
      </Text>
      <Card>
        <Segmented
          style={{ marginBottom: 16 }}
          value={status}
          onChange={(v) => setStatus(String(v))}
          options={[
            { value: 'OPEN', label: 'Open' },
            { value: 'CLOSED', label: 'Closed' },
            { value: 'ALL', label: 'All' },
          ]}
        />
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data}
          scroll={{ x: 1300 }}
          pagination={{ defaultPageSize: 20 }}
        />
      </Card>
    </div>
  );
};

export default TmmdeImpactListPage;
